import { useEffect, useRef, useState, useMemo, useCallback, Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { Dropdown, Modal, Select, Tooltip } from 'antd';


import './HeaderNav.css';
import { OogwaiLogo2Full, SettingsIcon, NotificationIcon, MenuIcon, BurgerMenuTwoIcon, UserIcon, CloseIcon, PlusIcon, NewConversationIcon, IntegrationIcon, LogoutIcon, HelpIcon } from "../Icons/iconImages";

import { logOut } from "../../store/slice/userAuth";
import { useDispatch, useSelector } from "react-redux";
import { auth } from "../../firebase/firebase";
import { Button } from "../Buttons";
import { useGetGeneralSettingsQuery } from "../../services/settingsSlice";
import TeamSelector from "../TeamSelector";
import { useForm } from "react-hook-form";
import { useGetCustomersEmailQuery } from "../../services/createProfile";
import { baseApi } from "../../services/baseQuery";
import { isPropertyIdClear, updateNewConversation, updateTestMode } from "../../services/staticSlice";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { signOutCognitoUser } from "../../services/auth";
import ReactGA from "react-ga4";
import { ReloadOutlined } from "@ant-design/icons";
import Icons from "../Icons";
import { toast } from "../Toast/toast";
import { useGetCurrentPlanQuery } from "../../services/stripe";
import TourDebugHelper from "../OnboardingTour/TourDebugHelper";

const sizes = {
  headingxs: "text-[0.75rem] font-semibold",
  headinglg: "text-[1rem] font-semibold",
  textxs: "text-[0.75rem]font-semibold",
}

const HeaderNav = ({ children, className = "", size = "headingxs", as, ...rest }) => {
  const Component = as || "h6";
  return (
    <Component className={`${sizes[size]} ${className}`} {...rest}>
      {children}
    </Component>
  )
}


const MainNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { control, handleSubmit } = useForm();
  const testMode = useSelector((state) => state.static.testMode);

  // Use workspace context
  const {
    currentWorkspace,
    workspaces,
    switchWorkspace,
    refreshWorkspaces,
    isLoading: isWorkspaceLoading,
    error: workspaceError,
    isInitialized
  } = useWorkspace();

  // Use sidebar context
  const { toggleMobileMenu, isMobileMenuOpen } = useSidebar();

  const { data: userData } = useGetGeneralSettingsQuery('', {
    refetchOnMountOrArgChange: true,
  });
  const getUserData = userData?.settings;

  // Get current plan data for query consumption
  const { data: getCurrentPlan } = useGetCurrentPlanQuery('');
  const tokenType = localStorage.getItem("oogw.ai_user_type") || "Bearer";

  const [activeTab, setActiveTab] = useState(location.pathname);

  const getEmail = localStorage.getItem("oogw.ai_user_email") || getUserData?.email;

  const oogwaiUser = getEmail && getEmail !== "info@oogw.ai" && getEmail !== "demo@oogw.ai" ? getEmail?.split('@')[1]?.includes('oogw.ai') : false;
  const [shadowUser, setShadowUser] = useState(null);

  const { data: customersEmail } = useGetCustomersEmailQuery("", {
    skip: !oogwaiUser
  });

  const storedShadowUser = localStorage.getItem('oogw.ai_shadow_user_email');

  useEffect(() => {
    setShadowUser(storedShadowUser && storedShadowUser !== 'undefined' ? storedShadowUser : null);
  }, [storedShadowUser]);

  const [isSwitchUserModalVisible, setIsSwitchUserModalVisible] = useState(false);

  // Navigate to create profile if no workspaces
  useEffect(() => {
    console.log('HeaderNav useEffect - redirect check:', {
      isInitialized,
      workspacesLength: workspaces.length,
      workspaces,
      currentPath: location.pathname,
      willRedirect: isInitialized && workspaces.length === 0 && location.pathname !== '/integrations'
    });

    // Don't redirect if we're already on integrations page (likely just created a profile)
    if (isInitialized && workspaces.length === 0 && location.pathname !== '/integrations') {
      console.log('HeaderNav: Redirecting to /create-profile');
      // navigate('/create-profile');
    }
  }, [workspaces, isInitialized, navigate, location.pathname])

  const isNewLogin = location.pathname.includes('onboardingIntegration') || location.pathname.includes('create-profile');
  const isViewData = location.pathname.includes('integrations/custom-events-data') || location.pathname.includes('integrations/view-raw-data');
  const isIntegrationsPage = location.pathname === '/integrations';
  const isSupportPage = location.pathname.includes('/support');
  const hasSidebar = location.pathname.includes('/dashboard') || location.pathname.includes('/conversation') || location.pathname.includes('/company-settings') || isSupportPage || isIntegrationsPage;

  const tabs = useMemo(() => [
    { label: 'Dashboard', value: '/dashboard' },
    { label: 'Integrations', value: '/integrations' },
  ], []);
  const tabRefs = useRef([]);

  const [user, setUser] = useState(null);



  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser({
          email: user.profile_name,
          displayName: user.displayName,
          photoURL: user.photoURL,
        });
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  // Set the initial active tab based on the URL path
  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
    dispatch(logOut());
    const auth = getAuth();
    signOut(auth);
    localStorage.removeItem("oogw.ai_user");
    localStorage.removeItem("oogw.ai_user_type");
    localStorage.removeItem("workspace_id");
    localStorage.removeItem('oogw.ai_shadow_user_email');

    navigate("/login");
    window.location.reload();
    dispatch(baseApi.util.resetApiState());
    signOutCognitoUser();

    ReactGA.event('logout', {
      method: 'manual',
      user_type: 'existing_user',
      source_page: window.location.pathname,
    });
    console.log("User logged out successfully");

  }, [dispatch, navigate]);

  const notificationItems = [
    // {
    //   label: 'Notification 1',
    //   key: '1',
    // },
    // {
    //   label: 'Notification 2',
    //   key: '2',
    // },
  ];
  const notificationProps = {
    items: notificationItems
  }

  // User Dropdown
  const menuItems = useMemo(() => [
    {
      label: (
        <div className="flex items-center space-x-2">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="relative">
              {
                getUserData?.profile_picture ? <img src={getUserData?.profile_picture} alt="" className="w-12 h-12 rounded-full object-cover" loading="lazy" /> : <UserIcon className="w-9 h-9 p-1.5 text-[#535862] shadow-sm rounded-full border border-[#535862] bg-white" />
              }
              <div className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 border border-white rounded-full bg-green-500 clip-cutout" />
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">
              {getUserData?.first_name || getUserData?.last_name ? `${getUserData?.first_name || ""} ${getUserData?.last_name || ""}` : <span className="opacity-50">No Name</span>}
            </span>
            <span className="text-sm font-normal">{getUserData?.email}</span>
          </div>
        </div>
      ),
      key: '0',
    },
    {
      type: 'divider',
    },
    {
      label: (
        <Button
          icon={<IntegrationIcon className="w-4 h-4 text-[#535862]" />}
          type="link"
          size="small"
          onClick={() => navigate('/integrations')}
          className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
        >
          <span>Integrations</span>
        </Button>
      ),
      key: '1',
    },
    {
      label: (
        <Button
          icon={<SettingsIcon className="w-4 h-4 text-[#535862]" />}
          type="link"
          size="small"
          onClick={() => navigate('/company-settings/general')}
          className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
        >
          <span>Settings</span>
        </Button>
      ),
      key: '2',
    },
    {
      label: (
        <Button
          icon={<HelpIcon className="w-4 h-4 text-[#535862]" />}
          type="link"
          size="small"
          onClick={() => navigate('/support/report-issue')}
          className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
        >
          <span>Support</span>
        </Button>
      ),
      key: 'support',
    },
    {
      type: 'divider',
    },
    {
      label: (
        <Button
          icon={<LogoutIcon className="w-4 h-4 text-[#535862]" />}
          type="link"
          size="small"
          onClick={handleLogout}
          className="ant-dropdown-menu-button text-gray-900 flex items-center space-x-2 justify-start w-full"
        >
          <span>Sign out</span>
        </Button>
      ),
      key: '3',
    },
  ], [getUserData]);
  const menuProps = {
    items: menuItems,
  };

  const profileData = workspaces?.map((user) => {
    return {
      label: user.workspace_id, // Display name
      value: user._id, // MongoDB ID for identification
    }
  })



  const shadowUserOpt = useMemo(() => {
    return (customersEmail?.emails && customersEmail?.emails.length > 0 ? customersEmail?.emails : []).map((email) => ({
      label: email.length > 30 ? `${email.slice(0, 30)}...` : email,
      value: email,
      title: email
    }));
  }, [customersEmail?.emails]) || [];


  const updateData = profileData ? [
    ...profileData,
    { label: <Button color="primary" variant="outlined" icon={<PlusIcon />} onClick={() => navigate('/create-profile')}>Create Workspace</Button>, value: 'add-profile', className: 'p-3' }
  ] :
    [
      { label: '+ Create Workspace', value: 'add-profile' }

    ]

  // Profile Dropdown
  const profileItems = useMemo(() => {
    return [
      ...(workspaces?.map((user) => ({
        label: (
          <div
            disabled={currentWorkspace === user._id || isWorkspaceLoading}
            type="text"
            className="flex items-center gap-2 py-1 justify-center font-medium cursor-pointer"
            onClick={() => onSubmitSwithUser(user._id)}
          >
            {user.workspace_id}
          </div>
        ),
        key: user._id,
      })) || []),
      {
        label: (
          <Button
            type="link"
            icon={<PlusIcon />}
            onClick={() => navigate('/create-profile')}
          >
            <span className="pl-2">Add Workspace</span>
          </Button>
        ),
        key: 'add-profile',
      }
    ];
  }, [workspaces, isWorkspaceLoading, currentWorkspace]);

  const profileProps = {
    items: profileItems,
    selectable: true,
    defaultSelectedKeys: [currentWorkspace],
  };

  // Handle toggling the user modal
  const handleToggleUser = useCallback(() => {
    setIsSwitchUserModalVisible(true);
  }, []);


  const onSubmitSwithUser = useCallback(async (workspaceId) => {
    if (workspaceId && workspaceId !== currentWorkspace) {
      try {
        // Check if we're on conversation page with a chatId
        let wasOnConversationWithChat = false;
        const isOnConversation = location.pathname.startsWith('/conversation');

        if (isOnConversation) {
          // Check for chatId in path (e.g., /conversation/123) or query string
          const hasChatIdInPath = location.pathname.split('/').length > 2;
          const hasChatIdInQuery = location.search.includes('chatid=');

          if (hasChatIdInPath || hasChatIdInQuery) {
            wasOnConversationWithChat = true;
            // Navigate to clean conversation immediately
            navigate('/conversation', { replace: true });
          } else {
            wasOnConversationWithChat = true;
          }
        }

        // Immediately signal Conversation to reset if we were viewing it
        if (wasOnConversationWithChat) {
          dispatch(updateNewConversation(true));
          // Clear any persisted latest chat id to avoid restoring a stale chat
          try { localStorage.removeItem('latest_chat_id'); } catch (e) { /* ignore */ }
        }

        await switchWorkspace(workspaceId);

        // If user was on conversation page, ensure we stay on clean conversation
        if (wasOnConversationWithChat) {
          // small delay to give workspace context time to update, then ensure we're on conversation
          setTimeout(() => {
            navigate('/conversation', { replace: true });
          }, 200);
        }
        // Success toast is handled in the context
      } catch (error) {
        // Error toast is handled in the context
        console.error('Failed to switch workspace:', error);
      }
    }
  }, [switchWorkspace, currentWorkspace, location, navigate, dispatch]);


  const handleGoBack = useCallback(() => {
    const { pathname } = location;
    const newPathname = pathname.split('/').slice(0, -1).join('/');
    const pathsToRemove = ['custom-events-data', 'view-raw-data'];

    if (pathsToRemove.some(suffix => newPathname.endsWith(suffix))) {
      const newPathname2 = newPathname.split('/').slice(0, -1).join('/');
      navigate(newPathname2);
    } else {
      navigate(newPathname);
    }
  }, [location, navigate]);

  const handleChange = (value) => {
    if (value === 'add-profile') return navigate('/create-profile')
    onSubmitSwithUser(value)
    dispatch(isPropertyIdClear(true))
    let testModeSession = sessionStorage.getItem('oogwai_testMode_close');
    dispatch(updateTestMode(value === "Demo" && testModeSession !== "true" ? true : false))

  };

  return (
    <>
      {/* Mobile Menu Overlay and Sidebar for Integrations and Support Pages */}
      {(isIntegrationsPage || isSupportPage) && isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/70 z-40 md:hidden transition-opacity duration-300"
            onClick={toggleMobileMenu}
          />

          {/* Mobile Sidebar */}
          <div
            className="fixed top-0 left-0 h-full w-full bg-white z-50 transition-transform duration-300 ease-in-out md:hidden flex flex-col shadow-2xl"
            style={{ marginTop: testMode ? '6.5rem' : '4rem' }}
          >
            {/* Workspace Selector */}
            <div className="px-4 py-4 border-b border-gray-200" data-tour="workspace-selector">
              <p className="text-xs font-medium text-gray-500 mb-2">Workspace</p>
              <Select
                defaultOpen={true}
                onChange={handleChange}
                value={currentWorkspace || ""}
                className="w-full"
                dropdownStyle={{ width: 'auto' }}
                popupMatchSelectWidth={false}
                options={updateData}
                optionRender={(item) => (
                  <div className="flex items-center gap-2 py-1 justify-center cursor-pointer">
                    {item.value === 'add-profile' ? item.label : item.label}
                  </div>
                )}
                dropdownRender={(menu) => (
                  <div className="text-center">{menu}</div>
                )}
              />
            </div>

            {/* Impersonate Section - Only for Oogwai Users */}
            {oogwaiUser && customersEmail?.emails && customersEmail.emails.length > 0 && (
              <div className="px-4 py-4 border-b border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-2">Impersonate Account</p>
                <Select
                  showSearch
                  placeholder="Select Customer"
                  value={shadowUser || undefined}
                  onChange={async (value) => {
                    setShadowUser(value);
                    localStorage.setItem('oogw.ai_shadow_user_email', value);
                    sessionStorage.removeItem('referral_code');
                    await new Promise(resolve => setTimeout(resolve, 50));
                    dispatch(baseApi.util.resetApiState());
                    if (location.pathname.startsWith('/conversation')) {
                      dispatch(updateNewConversation(true));
                      try { localStorage.removeItem('latest_chat_id'); } catch (e) { /* ignore */ }
                      navigate('/conversation', { replace: true });
                    }
                    await refreshWorkspaces();
                    if (toggleMobileMenu) toggleMobileMenu();
                  }}
                  onClear={async () => {
                    setShadowUser(null);
                    localStorage.removeItem('oogw.ai_shadow_user_email');
                    sessionStorage.removeItem('referral_code');
                    await new Promise(resolve => setTimeout(resolve, 50));
                    dispatch(baseApi.util.resetApiState());
                    await refreshWorkspaces();
                    if (toggleMobileMenu) toggleMobileMenu();
                  }}
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  className="w-full"
                  dropdownStyle={{ width: 'auto' }}
                  popupMatchSelectWidth={false}
                  options={shadowUserOpt}
                  optionRender={(item) => (
                    <div className="flex items-center gap-2 py-1 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.data.title?.length > 30 ? (
                        <Tooltip title={item.data.title} placement="left">
                          {item.label}
                        </Tooltip>
                      ) : (
                        item.label
                      )}
                    </div>
                  )}
                />
              </div>
            )}

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* Profile Section at Bottom */}
            {getUserData && (
              <div className='px-4 pb-4 mb-14'>
                <div className="border border-gray-300 rounded-2xl bg-white">
                  <div className="w-full flex items-center gap-3 p-4">
                    <div className="relative">
                      {getUserData?.profile_picture ? (
                        <img
                          src={getUserData.profile_picture}
                          alt={getUserData?.first_name || 'User'}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <UserIcon className="w-10 h-10 p-2 text-gray-600 rounded-full border border-gray-300 bg-white" />
                      )}
                      <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-white rounded-full bg-green-500" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getUserData?.first_name || getUserData?.last_name
                          ? `${getUserData?.first_name || ""} ${getUserData?.last_name || ""}`.trim()
                          : getUserData?.email?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {getUserData?.email || ''}
                      </p>
                    </div>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            label: (
                              <Button
                                icon={<IntegrationIcon className="w-4 h-4 text-[#535862]" />}
                                type="link"
                                size="small"
                                onClick={() => {
                                  navigate('/integrations');
                                  if (toggleMobileMenu) toggleMobileMenu();
                                }}
                                className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
                              >
                                <span>Integrations</span>
                              </Button>
                            ),
                            key: 'integrations',
                          },
                          {
                            label: (
                              <Button
                                icon={<SettingsIcon className="w-4 h-4 text-[#535862]" />}
                                type="link"
                                size="small"
                                onClick={() => {
                                  navigate('/company-settings/general');
                                  if (toggleMobileMenu) toggleMobileMenu();
                                }}
                                className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
                              >
                                <span>Settings</span>
                              </Button>
                            ),
                            key: 'settings',
                          },
                          {
                            label: (
                              <Button
                                icon={<HelpIcon className="w-4 h-4 text-[#535862]" />}
                                type="link"
                                size="small"
                                onClick={() => {
                                  navigate('/support/report-issue');
                                  if (toggleMobileMenu) toggleMobileMenu();
                                }}
                                className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
                              >
                                <span>Support</span>
                              </Button>
                            ),
                            key: 'support',
                          },
                          {
                            type: 'divider',
                          },
                          {
                            label: (
                              <Button
                                type="text"
                                size="small"
                                onClick={() => {
                                  handleLogout();
                                  if (toggleMobileMenu) toggleMobileMenu();
                                }}
                                icon={<LogoutIcon className="w-4 h-4 text-[#535862]" />}
                                className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
                              >
                                <span>Sign out</span>
                              </Button>
                            ),
                            className: 'bg-gray-50',
                            key: 'signout',
                          }
                        ]
                      }}
                      trigger={['click']}
                      placement="topRight"
                      overlayClassName="user-menu-dropdown"
                      overlayStyle={{ zIndex: 9999 }}
                    >
                      <Button type="text" shape="circle">
                        <MenuIcon className="w-4 h-4 text-gray-400" />
                      </Button>
                    </Dropdown>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <header className={`fixed ${testMode ? 'top-[50px]' : 'top-0'} start-0 w-full z-50 ${isViewData ? 'border-none' : ''} safe-top`}>
        <div className="main-header pt-safe" data-tour="main-navigation">
        {
          isViewData ? <div className="flex justify-end"><Button type="text" onClick={handleGoBack} icon={<CloseIcon />} /></div> : <>
            {/* Mobile Layout */}
            <div className="flex md:hidden items-center justify-between w-full">
              {/* Left side - Hamburger menu */}
              <div className="w-12 flex items-center">
                {hasSidebar && toggleMobileMenu && (
                  <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                    aria-label="Toggle menu"
                  >
                    <BurgerMenuTwoIcon className="w-5 h-5 text-gray-600" />
                  </button>
                )}
              </div>

              {/* Center - Logo */}
              <div className="header-logo flex-1 flex justify-center">
                <Link to="/dashboard" className="relative">
                  <OogwaiLogo2Full className="h-[2rem]" />
                  <span className="absolute bottom-[-5px] text-[0.7rem] border border-gray-300 bg-gray-200 text-gray-900 rounded-full px-1">Beta</span>
                </Link>
              </div>

              {/* Right side - Empty for balance */}
              <div className="w-12 md:visible hidden"></div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center gap-2">
              <div className="header-logo">
                <Link to="/dashboard" className="relative">
                  <OogwaiLogo2Full className="h-[2rem]" />
                  <span className="absolute bottom-[-5px] text-[0.7rem] border border-gray-300 bg-gray-200 text-gray-900 rounded-full px-1">Beta</span>
                </Link>
              </div>
            </div>
            {!isNewLogin && <div className={`tabs items-center w-full pl-16 lg:pl-28 pr-1 gap-1 lg:gap-2 flex`} data-tour="workspace-selector">
              <Fragment>
                {/* <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} tabRefs={tabRefs} /> */}
                <p className="text-[#414651] font-medium text-xs lg:text-sm">Workspace</p>
                <Select
                  onChange={handleChange}
                  options={updateData}
                  value={currentWorkspace || ""}
                  dropdownStyle={{ width: 'auto' }}
                  popupMatchSelectWidth={false}
                  optionRender={(item => {
                    return (
                      <div className="flex items-center gap-2 py-1 justify-center cursor-pointer">
                        {item.value === 'add-profile' ? item.label : item.label}
                      </div>
                    )
                  })}
                  dropdownRender={(menu) => (
                    <div className="text-center">
                      {menu}
                    </div>
                  )}
                  className="min-w-24 lg:min-w-36 text-center font-semibold text-[#414651] text-xs lg:text-sm"
                />
              </Fragment>
            </div>}

            {oogwaiUser && <div className="tabs flex items-center w-full justify-end pr-2 lg:pr-4 gap-1 lg:gap-2">
              <>
                <p className="text-[#414651] font-medium text-xs lg:text-sm">Impersonate</p>

                <Select
                  onChange={async (value) => {
                    // Store the shadow user first so components can detect the change
                    setShadowUser(value);
                    localStorage.setItem('oogw.ai_shadow_user_email', value);
                    sessionStorage.removeItem('referral_code');

                    // Small delay to ensure localStorage change is propagated
                    await new Promise(resolve => setTimeout(resolve, 50));

                    // Reset API state after setting shadow user
                    dispatch(baseApi.util.resetApiState());

                    // If we're on conversation page, trigger a new conversation to clear the current chat
                    const isOnConversation = location.pathname.startsWith('/conversation');
                    if (isOnConversation) {
                      dispatch(updateNewConversation(true));
                      // Clear any persisted latest chat id to avoid restoring a stale chat
                      try { localStorage.removeItem('latest_chat_id'); } catch (e) { /* ignore */ }
                      // Navigate to clean conversation URL without chatid (handles both /conversation/123 and /conversation?chatid=123)
                      navigate('/conversation', { replace: true });
                    }

                    // Don't remove workspace_id - let it be fetched for the impersonated user
                    // Force refresh workspaces to get the impersonated user's workspaces
                    await refreshWorkspaces();
                    // Navigate to dashboard to load the impersonated user's workspace
                    if (location.pathname === '/create-profile') {
                      setTimeout(() => {
                        navigate('/dashboard');
                      }, 100);
                    }
                  }}
                  variant="borderless"
                  options={shadowUserOpt}
                  value={shadowUser}
                  dropdownStyle={{ maxWidth: '300px', minWidth: '200px' }}
                  popupMatchSelectWidth={false}
                  allowClear
                  placeholder="Select Customer "
                  onClear={async () => {
                    // Clear shadow user first so components can detect the change
                    setShadowUser(null);
                    localStorage.removeItem('oogw.ai_shadow_user_email');
                    sessionStorage.removeItem('referral_code');

                    // Small delay to ensure localStorage change is propagated
                    await new Promise(resolve => setTimeout(resolve, 50));
                    
                    // Reset API state after clearing shadow user
                    dispatch(baseApi.util.resetApiState());
                    
                    // If we're on conversation page, trigger a new conversation to clear the current chat
                    const isOnConversation = location.pathname.startsWith('/conversation');
                    if (isOnConversation) {
                      dispatch(updateNewConversation(true));
                      // Clear any persisted latest chat id to avoid restoring a stale chat
                      try { localStorage.removeItem('latest_chat_id'); } catch (e) { /* ignore */ }
                      // Navigate to clean conversation URL without chatid (handles both /conversation/123 and /conversation?chatid=123)
                      navigate('/conversation', { replace: true });
                    }
                    
                    // Force refresh to get original user's workspaces
                    await refreshWorkspaces();
                    // If we're on create-profile and the original user has workspaces, navigate to dashboard
                    // The WorkspaceContext will handle loading the correct workspace
                    if (location.pathname === '/create-profile') {
                      // Small delay to allow workspace context to update
                      setTimeout(() => {
                        navigate('/dashboard');
                      }, 100);
                    }
                  }}
                  showSearch
                  optionRender={(item => {
                    return (
                      <div className="flex items-center gap-2 py-1 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap">
                        {item.data.title.length > 30 ? (
                          <Tooltip title={item.data.title} placement="left">
                            {item.label}
                          </Tooltip>
                        ) : (
                          item.label
                        )}
                      </div>
                    )
                  })}
                  dropdownRender={(menu) => (
                    <div className="text-center">
                      {menu}
                    </div>
                  )}
                  className="min-w-24 lg:min-w-36 font-semibold text-[#414651] text-xs lg:text-sm"
                />
              </>
            </div>}

            <div className="actions">
              {!isNewLogin ? (
                <div className="flex items-center gap-0.5 lg:gap-2 relative">
                  {/* <button
                    className={`flex items-center gap-1 text-sm font-medium py-2 px-3 rounded-full text-gray-00 ${activeTab.indexOf('/company-settings') !== -1 ? 'text-green-500' : ''}`}
                    onClick={() => navigate('/company-settings/general')}>
                    <SettingsIcon className={`w-4 h-4 ${activeTab.indexOf('/company-settings') !== -1 ? 'text-green-500' : 'text-gray-800'}`} />
                  </button> */}
                  {/* <Tooltip title="Reset onboarding tours" placement="bottom">
                    <Button
                      type="text"
                      shape="circle"
                      className="group"
                      icon={<div className="relative">
                        <Icons icon="uiw:map" width="18" height="18" className="text-gray-800 group-hover:opacity-0 transition-opacity" />
                        <Icons icon="ant-design:reload-outlined" width="18" height="18" className="text-gray-800 absolute top-0 left-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>}
                      onClick={() => {
                        localStorage.removeItem('oogwai_onboarding_tours');
                        toast.info('All tours reset! Reloading...');
                        setTimeout(() => window.location.reload(), 1000);
                      }}
                    />
                  </Tooltip> */}
                  {/* Query Counter - Now visible on all screens */}
                  {tokenType !== "Shopify" && (() => {
                    const queryCount = getCurrentPlan?.query_count ?? 0;
                    const queryLimit = getCurrentPlan?.query_limit ?? 0;
                    const percentage = queryLimit > 0 ? (queryCount / queryLimit) * 100 : 0;

                    return (
                      <Tooltip title="Queries consumed / Total queries (Click to view plan)" placement="bottom">
                        <div
                          onClick={() => navigate('/company-settings/plan')}
                          className="flex flex-wrap min-[400px]:flex-nowrap items-center gap-1 md:gap-1.5 cursor-pointer"
                        >
                          <p className="text-[10px] md:text-xs lg:text-base text-[#535862]">Usage</p>
                          <span className={`px-1.5 md:px-2 py-1 rounded-md border text-[10px] md:text-xs font-medium transition-colors ${

                               'bg-gray-50 border-gray-200 text-[#414651] hover:bg-[#F5FAF7] hover:text-[#4C8067]'
                          }`}>
                            {queryCount}/{queryLimit}
                          </span>
                        </div>
                      </Tooltip>
                    );
                  })()}
                    <Button size="small" onClick={() => {
                      navigate('/conversation');
                      dispatch(updateNewConversation(true))
                       localStorage.removeItem('latest_chat_id');
                    }} className="py-0 px-1.5 lg:px-4 text-[10px] lg:text-xs" icon={<NewConversationIcon className="w-3 h-3 lg:w-4 lg:h-4" />} type="primary">
                      <span className="inline lg:inline">Ask Oogwai</span>
                    </Button>
                  {/* <Dropdown className="" menu={notificationProps} trigger={['click']} placement="bottomRight">
                    <Button type="text" icon={<NotificationIcon />} />
                  </Dropdown> */}
                  <Dropdown className="hidden md:block" menu={menuProps} trigger={['click']} placement="bottomRight" data-tour="help-menu">
                    <Button type="text" icon={<MenuIcon />} />
                  </Dropdown>
                  {/* <p className="text-[#414651] font-medium text-sm">Workspace</p> */}

                </div>
              ) : (
                <div data-tour="help-menu">
                  {
                    !location.pathname.includes('create-profile') &&
                    <Button
                      type="text"
                      iconPosition="left"
                      onClick={() => navigate('/dashboard')}
                    >
                      Skip
                    </Button>
                  }
                </div>
              )}
            </div>
            <Modal
              open={isSwitchUserModalVisible}
              onOk={() => onSubmitSwithUser()}
              onCancel={() => setIsSwitchUserModalVisible(false)}
              centered
              footer={null}
            >
              {workspaces?.length > 0 ? <div className='grid grid-cols-1 mt-8 p-4 divide-y-2 w-full'>
                {
                  workspaces.map((user, index) => (
                    <TeamSelector
                      key={index}
                      label={user.workspace_id}
                      subLabel={user.profile_description || ''}
                    >
                      <div className='flex items-center space-x-2'>
                        <Button disabled={currentWorkspace === user._id || isWorkspaceLoading} type="text" className={currentWorkspace === user._id ? 'text-green-500' : ''} onClick={() => onSubmitSwithUser(user._id)}>{currentWorkspace === user._id ? 'Selected' : 'Select'}</Button>
                      </div>
                    </TeamSelector>
                  ))
                }
              </div> :
                <div className='flex justify-center my-4'>
                  <p className='text-center'>No profiles found</p>
                </div>
              }
              <div className='flex justify-center my-4'>
                <Button type="default" onClick={() => {
                  navigate('/create-profile')
                  setIsSwitchUserModalVisible(false)
                }}
                  icon={<PlusIcon />}
                >
                  Create Workspace
                </Button>
              </div>
            </Modal>
          </>
        }
      </div>
    </header >
    <TourDebugHelper />
    </>
  );
}

export { HeaderNav, MainNavbar };